import React from 'react';
import {RootState} from "../../../../../reducers";
import {getCart} from "../../../../services/Cart/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import RoundedIcon from "../../../Common/RoundedIcon";
import {ReactComponent as RoomServiceIco} from "../../../../../images/icons/room-service.svg";
import {ReactComponent as DropdownIco} from "../../../../../images/icons/arrow-bottom.svg";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {anyMenuHasPickDate} from "../../utils";
import {OrderType} from "../../../../services/Api/types";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import {getAvailabilities, getMenus} from "../../../../services/Menu/reducer";
import {DateHelper} from "../../../../services/Common/Date/DateHelper";
import StoreUtils from "../../../../services/Store/StoreUtils";

interface OwnProps {
    handleShowOrderDetailsModal: () => void;
    handleOnKeyDown: (event: (React.KeyboardEvent<HTMLDivElement> | KeyboardEvent)) => Promise<void>;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const CartButtonRoomServiceInfo = ({
                                       session,
                                       config,
                                       locale,
                                       handleShowOrderDetailsModal,
                                       availabilities,
                                       menus,
                                       handleOnKeyDown
                                   }: Props) => {
    const {t} = useTranslation();
    const isStoreOpen = HoursHelper.isOpen(config?.hours, DateService.now());
    const orderTypeMenuAvailabilities = AvailableHelper.getAvailabilities(availabilities, menus.map((menu: any) => menu[OrderType.ROOM_SERVICE.toLowerCase()] && menu.availability_id));

    const getInfoDetails = (): {
        topInfo: string | JSX.Element;
        topInfoColor: string;
        bottomInfo: string | JSX.Element
    } => {
        const pickDateAvailable = anyMenuHasPickDate(menus, OrderType.ROOM_SERVICE);

        if (config && !StoreUtils.getOrderTypes(config).includes(OrderType.ROOM_SERVICE)) {
            return {
                topInfo: (
                    <span className="font-weight-bold">
                        {t("common.word.not_available").toLowerCase()}
                    </span>
                ),
                topInfoColor: "text-danger",
                bottomInfo: <span className="text-primary">
                     {t("modules.cart.field.change_order_type.title")}
                    </span>
            }
        }

        if (!session.pickup_at) {
            if (isStoreOpen) {
                const isOpenForCurrentType = HoursHelper.isOpen(orderTypeMenuAvailabilities.flatMap((availability: any) => availability.hours), DateService.now())
                    && orderTypeMenuAvailabilities.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.now()));

                if (isOpenForCurrentType) {
                    return {
                        topInfo: t("common.word.as_soon_as_possible").toLowerCase(),
                        topInfoColor: "text-success",
                        bottomInfo: getRoomInfo()
                    }
                } else {
                    const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(orderTypeMenuAvailabilities)?.dateFrom;
                    const date = nearestOpenHours ? DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "") : undefined;

                    let bottomInfo: JSX.Element = <></>;

                    if (StoreUtils.isPickDateEnabled(config)) {
                        bottomInfo = <span
                            className="text-primary">{t("modules.cart.field.change_date_or_order_type.title")}.</span>
                    } else {
                        bottomInfo = date ? <span
                            className="text-primary">{t("modules.cart.field.change_order_type.title", {date})}</span> : <></>
                    }

                    return {
                        topInfo: t("modules.cart.field.not_available_now.title"),
                        topInfoColor: "text-danger",
                        bottomInfo
                    }
                }
            } else {
                const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(orderTypeMenuAvailabilities)?.dateFrom;
                const date = nearestOpenHours ? DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "") : undefined;
                const topInfo = t("modules.cart.field.closed_now.title");
                const topInfoColor = "text-danger";

                if (pickDateAvailable) {
                    return {
                        topInfo,
                        topInfoColor,
                        bottomInfo: <span
                            className="text-primary">{`${t("modules.cart.field.change_date_to_order.title")}.`}</span>
                    }
                }

                return {
                    topInfo,
                    topInfoColor,
                    bottomInfo: <span className="text-muted">{t("modules.cart.field.open_at.title", {date})}</span>
                }
            }
        } else {
            if (!pickDateAvailable && session.pickup_at) {
                return {
                    topInfo: (
                        <span className="font-weight-bold">
                                {t("modules.cart.field.pickup_at_unavailable.title")}
                            </span>
                    ),
                    topInfoColor: "text-danger",
                    bottomInfo: <span className="text-primary">
                     {t("modules.cart.field.change_date.title")}
                    </span>
                }
            }

            return {
                topInfo: DateHelper.getFormattedNearestDate(session.pickup_at, t, locale ?? ""),
                topInfoColor: "text-success",
                bottomInfo: getRoomInfo()
            };
        }
    }

    const getRoomInfo = () => {
        if (session.room_number) {
            return `${t("modules.cart.field.room.title")}: ${session.room_number}`;
        }

        return <span className="text-primary">{t("common.action.choose_room")}</span>
    }

    const drawFullInfo = () => {
        const detailsInfo = getInfoDetails();

        return (
            <>
                <div>
                    <span className="font-weight-bold">{`${t("enums.order.type.ROOM_SERVICE")}: `}</span>
                    <span className={`${detailsInfo.topInfoColor} font-weight-bold`}>{detailsInfo.topInfo}</span>
                </div>
                <div>{detailsInfo.bottomInfo}</div>
            </>
        )
    }

    return (
        <div className="cart-button-info" onClick={handleShowOrderDetailsModal} tabIndex={0} role="button"
             onKeyDown={handleOnKeyDown}>
            <RoundedIcon icon={<RoomServiceIco/>} size="medium" className="text-primary"/>
            <div className="cart-button-info-middle-box">
                {drawFullInfo()}
            </div>
            <DropdownIco className="ms-auto dropdown-ico"/>
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    cart: getCart(state.cart),
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    locale: selectLocale(state.session),
    availabilities: getAvailabilities(state.menu),
    menus: getMenus(state.menu),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartButtonRoomServiceInfo));